import { useState, useCallback, useRef } from 'react';
import {
  type InvokeActionFn,
  type MasonryEngineNodeAction,
} from '@leagueplatform/masonry-engine';
import { type MasonryDriverAction } from '#types/masonry-driver-node-actions.ts';
import { useInvokeAction } from './use-invoke-action.hook';

type UseInvokeActionWithLoadingStateResult<
  Action extends MasonryEngineNodeAction,
> = {
  invokeAction: (
    action: Parameters<InvokeActionFn<Action, true>>[0],
  ) => ReturnType<InvokeActionFn<Action, true>>;
  isLoading: boolean;
};

export const useInvokeActionWithLoadingState = <
  Action extends MasonryEngineNodeAction = MasonryDriverAction,
>(): UseInvokeActionWithLoadingStateResult<Action> => {
  const [isLoading, setIsLoading] = useState(false);
  const inFlightCountRef = useRef(0);
  const invokeAction = useInvokeAction<Action>();

  const invokeActionWithLoadingState = useCallback(
    async (action: Parameters<typeof invokeAction>[0]) => {
      inFlightCountRef.current += 1;
      setIsLoading(true);
      try {
        return await Promise.resolve(invokeAction(action));
      } finally {
        inFlightCountRef.current -= 1;
        if (inFlightCountRef.current === 0) {
          setIsLoading(false);
        }
      }
    },
    [invokeAction],
  );

  return { invokeAction: invokeActionWithLoadingState, isLoading };
};
